/**
 * Settings → Invoicing → Message templates (design L): one editor for every
 * template key × channel × language. It edits the shop's overrides in the
 * settings form; built-in texts come from settings.templateDefaults and are
 * never edited or sent back. The slot logic lives in settingsModel.
 */
import React, { useRef } from 'react';
import {
  MAX_TEMPLATE_LENGTH, TEMPLATE_CHANNELS, TEMPLATE_CHIPS, TEMPLATE_KEYS, TEMPLATE_LANGS,
  fillTemplate, hasOverride, insertAtCursor, removeTemplate, resolveTemplate, sampleVariables, setTemplate, smsSegments,
  templatePath, unknownVariables,
  type TemplateChannel, type TemplateDefaults, type TemplateKey, type TemplateLang, type Templates,
} from '../settingsModel';

export interface TemplateSlot { key: TemplateKey; ch: TemplateChannel; lang: TemplateLang }

const SOURCE_LABEL = {
  override: 'Edited',
  default: 'Default',
  whatsapp: 'Using the WhatsApp text',
  english: 'Using the English text',
} as const;

const focusRing = 'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)]';

function Segmented<T extends string>({ label, options, value, onChange }: { label: string; options: { key: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex gap-0.5 p-[3px] rounded-lg bg-surface2 border border-borderc">
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.key)}
            className={`h-7 px-2.5 rounded-md text-[12px] font-semibold ${focusRing} ${on ? 'bg-surface text-text1' : 'text-text2 hover:text-text1'}`}
            style={on ? { boxShadow: '0 1px 2px rgba(0,0,0,.12)' } : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function TemplateEditor({
  templates, defaults, slot, setSlot, onChange, err, touch, shopName, shopPhone, footer,
}: {
  templates: Templates;
  defaults?: TemplateDefaults;
  slot: TemplateSlot;
  setSlot: (s: TemplateSlot) => void;
  onChange: (t: Templates) => void;
  err: (path: string) => string | undefined;
  touch: (path: string) => void;
  shopName?: string;
  shopPhone?: string;
  footer: React.ReactNode;
}) {
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const lastField = useRef<'text' | 'subject'>('text');

  const { key, ch, lang } = slot;
  const path = templatePath(key, ch, lang);
  const isEmail = ch === 'EMAIL';
  const resolved = resolveTemplate(templates, defaults, key, ch, lang);
  const edited = hasOverride(templates, key, ch, lang);
  const text = resolved.text;
  const subject = resolved.subject ?? '';
  const textErr = err(path);
  const subjectErr = err(`${path}.subject`);
  const unknown = unknownVariables(isEmail ? `${subject} ${text}` : text);
  const missingDefaults = !defaults;

  const write = (next: { text?: string; subject?: string }) => {
    onChange(setTemplate(templates, defaults, key, ch, lang, { text, subject, ...next }));
    touch(path);
    if (isEmail) touch(`${path}.subject`);
  };

  const insert = (token: string) => {
    const intoSubject = isEmail && lastField.current === 'subject';
    const el = intoSubject ? subjectRef.current : textRef.current;
    const value = intoSubject ? subject : text;
    const r = insertAtCursor(value, el?.selectionStart, el?.selectionEnd, token);
    write(intoSubject ? { subject: r.value } : { text: r.value });
    window.requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(r.caret, r.caret);
    });
  };

  const vars = sampleVariables(shopName, lang, shopPhone);
  const filled = fillTemplate(text, vars);
  const filledSubject = fillTemplate(subject, vars);
  const sms = smsSegments(filled);
  const count = ch === 'SMS'
    ? `${sms.characters} characters · ${sms.segments} ${sms.segments === 1 ? 'segment' : 'segments'}${sms.encoding === 'UCS2' ? ' · Unicode' : ''}`
    : ch === 'WHATSAPP' ? `${filled.length} characters` : 'Free';

  const keyHasOverrides = (k: TemplateKey) => Object.values(templates[k] || {}).some((byLang) => Object.keys(byLang || {}).length > 0);

  return (
    <div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start">
      <div className="bg-surface border border-borderc rounded-card">
        <div className="p-5 flex flex-col gap-3.5">
          <span className="text-[16px] font-semibold text-text1">Message templates</span>

          <div role="radiogroup" aria-label="Template" className="flex gap-1.5 flex-wrap">
            {TEMPLATE_KEYS.map((t) => {
              const on = t.key === key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setSlot({ ...slot, key: t.key })}
                  className={`h-8 px-3 rounded-full border text-[13px] font-semibold flex items-center gap-1.5 ${focusRing} ${on ? 'border-brand bg-brand-soft text-brand' : 'border-borderc2 bg-surface text-text2 hover:bg-surface2'}`}
                >
                  {t.label}
                  {keyHasOverrides(t.key) && <span className="w-1.5 h-1.5 rounded-full bg-brand" title="Has your edits" aria-label="Has your edits" />}
                </button>
              );
            })}
          </div>

          <div className="flex gap-3 flex-wrap">
            <Segmented label="Channel" options={TEMPLATE_CHANNELS} value={ch} onChange={(v) => setSlot({ ...slot, ch: v })} />
            <Segmented label="Language" options={TEMPLATE_LANGS} value={lang} onChange={(v) => setSlot({ ...slot, lang: v })} />
          </div>
          {ch === 'WHATSAPP' && (
            <span className="text-[12px] text-text3">The WhatsApp text is also what “Share from my device” sends.</span>
          )}

          {missingDefaults && (
            <span role="alert" className="text-[12px]" style={{ color: 'var(--warnFg)' }}>
              The built-in texts did not load. Reload the page to see them.
            </span>
          )}

          {isEmail && (
            <label className="flex flex-col min-w-0">
              <span className="nl-label">Subject</span>
              <input
                ref={subjectRef}
                className="nl-input font-mono"
                value={subject}
                maxLength={240}
                spellCheck={false}
                aria-invalid={!!subjectErr}
                style={subjectErr ? { borderColor: 'var(--neg)' } : undefined}
                onFocus={() => { lastField.current = 'subject'; }}
                onBlur={() => touch(`${path}.subject`)}
                onChange={(e) => write({ subject: e.target.value })}
              />
              {subjectErr && <span role="alert" className="text-[12px] text-neg mt-1">{subjectErr}</span>}
            </label>
          )}

          <div className="flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <label htmlFor="tpl-text" className="nl-label !mb-0 flex items-center gap-2">
                Message
                <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${edited ? 'bg-brand-soft text-brand' : 'bg-surface2 text-text3'}`}>
                  {SOURCE_LABEL[resolved.source]}
                </span>
              </label>
              {edited && (
                <button type="button" className="nl-btn nl-btn--ghost nl-btn--sm" onClick={() => onChange(removeTemplate(templates, key, ch, lang))}>
                  Reset to default
                </button>
              )}
            </div>
            <textarea
              id="tpl-text"
              ref={textRef}
              className="nl-input font-mono"
              rows={isEmail ? 7 : 5}
              value={text}
              spellCheck={false}
              aria-invalid={!!textErr}
              aria-describedby="tpl-text-help"
              style={{ resize: 'vertical', lineHeight: 1.5, ...(textErr ? { borderColor: 'var(--neg)' } : {}) }}
              onFocus={() => { lastField.current = 'text'; }}
              onBlur={() => touch(path)}
              onChange={(e) => write({ text: e.target.value })}
            />
            <div id="tpl-text-help" className="flex justify-between gap-3 mt-1">
              <span className="flex flex-col gap-0.5">
                {textErr && <span role="alert" className="text-[12px] text-neg">{textErr}</span>}
                {unknown.length > 0 && (
                  <span className="text-[12px]" style={{ color: 'var(--warnFg)' }}>
                    {unknown.join(' ')} {unknown.length === 1 ? 'is not a variable' : 'are not variables'} and will be sent as typed.
                  </span>
                )}
              </span>
              <span className={`text-[12px] nl-num whitespace-nowrap ${text.length > MAX_TEMPLATE_LENGTH ? 'text-neg' : 'text-text3'}`}>
                {text.length} / {MAX_TEMPLATE_LENGTH}
              </span>
            </div>
          </div>

          <div className="flex gap-1.5 flex-wrap items-center">
            <span className="text-[12px] text-text3 mr-0.5">Insert</span>
            {TEMPLATE_CHIPS.map((c) => (
              <button
                key={c.label}
                type="button"
                aria-label={`Insert ${c.label}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(c.insert)}
                className={`h-[26px] px-2 rounded-md border border-brand bg-brand-soft text-brand font-mono text-[11.5px] ${focusRing}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {ch !== 'EMAIL' && <span className="text-[12px] text-text3">SMS and WhatsApp messages must include {'{link}'}.</span>}

          {ch === 'WHATSAPP' && (
            <div className="px-3.5 py-3 rounded-[10px] bg-info-soft text-[12px]" style={{ color: 'var(--infoFg)' }}>
              WhatsApp messages sent by Netlise use templates approved by Meta. Your edits apply to messages you share from your device; Netlise-sent WhatsApp uses the approved wording.
            </div>
          )}
        </div>
        {footer}
      </div>

      <div className="rounded-card border border-borderc bg-surface2 p-[18px] flex flex-col gap-2.5 min-[1100px]:sticky min-[1100px]:top-0">
        <span className="text-[11px] font-semibold tracking-[.08em] text-text3">PREVIEW · {vars.number}</span>
        {isEmail && filledSubject && (
          <span className="text-[13px] font-semibold text-text1 break-words">{filledSubject}</span>
        )}
        <div
          aria-live="polite"
          className="self-start max-w-full px-3.5 py-3 text-[14px] leading-[1.5] whitespace-pre-wrap break-words"
          style={{
            background: ch === 'SMS' ? '#E9E9EE' : '#FFFFFF',
            color: '#131318',
            borderRadius: '14px 14px 14px 4px',
            boxShadow: '0 1px 2px rgba(0,0,0,.08)',
          }}
        >
          {filled || ' '}
        </div>
        <span className="text-[12px] text-text2 nl-num">{count}</span>
        <span className="text-[12px] text-text3">Sample values: Nimal Perera, {vars.amount}, due {vars.dueDate}.</span>
      </div>
    </div>
  );
}
